import { useState } from "react";
import { useNavigate } from "react-router-dom";
// MdDirectionsBus is only used by the hidden LagBus entries below — re-add it
// to this import when restoring them.
import { MdFlight, MdTrain, MdCreditCard } from "react-icons/md";
import { FaBus } from "react-icons/fa6";
import FlightGroup from "../Flight/FlightGroup";
import BusGroup from "./BusGroup";
import TransitGroup from "./TransitGroup";
import CowryGroup from "./CowryGroup";

const ORANGE = "#FF6A1A";

const TABS = [
  { key: "flight", label: "Flight", icon: MdFlight },
  { key: "interstate", label: "Interstate", icon: FaBus },
  // LagBus is hidden for now — restoring this line brings the tab back:
  // { key: "lagbus", label: "LagBus", icon: MdDirectionsBus },
  { key: "train", label: "Train", icon: MdTrain },
  { key: "cowry", label: "Cowry", icon: MdCreditCard },
] as const;

const MOBILE_CARDS = [
  { to: "/flights", label: "Flights", blurb: "Search 900+ airlines", cta: "Search now", icon: MdFlight },
  { to: "/interstate", label: "Interstate", blurb: "Interstate bus trips", cta: "Search now", icon: FaBus },
  // LagBus is hidden for now:
  // { to: "/lagbus", label: "LagBus", blurb: "Lagos commuter buses", cta: "Get routes", icon: MdDirectionsBus },
  { to: "/train", label: "Train", blurb: "Lagos rail lines", cta: "Get routes", icon: MdTrain },
  { to: "/cowry", label: "Cowry", blurb: "Top up your card", cta: "Fund now", icon: MdCreditCard },
] as const;

const ArrowIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14M12 5l7 7-7 7" />
  </svg>
);

const Banner = () => {
  const [currGroup, setCurrGroup] = useState("flight");
  const navigate = useNavigate();

  return (
    <section className="relative" style={{ background: "#0B1030" }}>
      <style>{`
        @keyframes riseH { to { opacity:1; transform:translateY(0); } }
        .h-rise { opacity:0; transform:translateY(20px); animation:riseH .82s cubic-bezier(.16,1,.3,1) forwards; }
        .hd1{animation-delay:.05s} .hd2{animation-delay:.2s} .hd3{animation-delay:.35s} .hd4{animation-delay:.5s}

        .liquid-glass-card {
          background:rgba(255,255,255,0.13);
          backdrop-filter:blur(52px) saturate(180%) brightness(112%);
          -webkit-backdrop-filter:blur(52px) saturate(180%) brightness(112%);
          border:1px solid rgba(255,255,255,0.3);
          box-shadow:
            inset 0 1.5px 0 rgba(255,255,255,0.52),
            inset 0 -1px 0 rgba(0,0,0,0.04),
            0 0 0 0.5px rgba(255,255,255,0.08),
            0 32px 80px rgba(0,0,0,0.42);
        }
      `}</style>

      {/* Decorative background layers — clipped to the hero bounds only, so popovers in the content below aren't cut off */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Background photo + overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="/bg_new.jpeg"
            alt=""
            className="w-full h-full object-cover"
            style={{ objectPosition: "center 55%" }}
          />
          <div
            className="absolute inset-0"
            style={{ background: "rgba(11,16,48,0.82)" }}
          />
        </div>

        {/* Dot grid */}
        <div
          className="absolute inset-0"
          style={{
            opacity: 0.18,
            backgroundImage: "radial-gradient(rgba(255,255,255,.5) 1px,transparent 1px)",
            backgroundSize: "34px 34px",
            maskImage: "linear-gradient(180deg,transparent,#000 30%,#000 70%,transparent)",
            WebkitMaskImage: "linear-gradient(180deg,transparent,#000 30%,#000 70%,transparent)",
          }}
        />

        {/* Orange glow orb */}
        <div
          className="absolute"
          style={{
            right: -120, top: 60,
            width: 420, height: 420,
            borderRadius: "50%",
            background: "radial-gradient(circle,rgba(255,150,60,.55),rgba(255,120,30,0) 62%)",
            filter: "blur(8px)",
          }}
        />
      </div>

      {/* Content */}
      <div
        className="relative z-10 max-w-[1160px] mx-auto px-5 md:px-12"
        style={{ paddingTop: "clamp(110px,14vw,148px)", paddingBottom: 64 }}
      >
        {/* Eyebrow */}
        <div
          className="h-rise hd1 inline-flex items-center gap-2 mb-5"
          style={{ color: "#FFCFA8", fontSize: 12.5, fontWeight: 700, letterSpacing: ".6px", textTransform: "uppercase" }}
        >
          <span
            style={{
              width: 7, height: 7, borderRadius: "50%",
              background: ORANGE,
              boxShadow: "0 0 0 4px rgba(255,106,26,.25)",
              display: "inline-block", flexShrink: 0,
            }}
          />
          Stavigo
        </div>

        {/* Heading */}
        <h1
          className="h-rise hd2 text-white leading-none"
          style={{
            fontFamily: '"Bricolage Grotesque", sans-serif',
            fontSize: "clamp(38px,5.6vw,70px)",
            fontWeight: 800,
            letterSpacing: -2,
            maxWidth: 680,
            marginBottom: 14,
          }}
        >
          Travel smarter.
          <br />
          Book{" "}
          <em style={{ fontStyle: "normal", color: ORANGE }}>faster</em>.
        </h1>

        <p
          className="h-rise hd3"
          style={{
            color: "rgba(255,255,255,.70)",
            fontSize: "clamp(14px,1.4vw,17px)",
            maxWidth: 480,
            lineHeight: 1.65,
            marginBottom: 36,
          }}
        >
          Flights and interstate bus trips — compare, book, and travel across Nigeria and beyond.
        </p>

        {/* ── Desktop: glass card with original floating pill tab ── */}
        <div
          className="liquid-glass-card hidden md:flex flex-col items-center h-rise hd4"
          style={{
            position: "relative",
            borderRadius: 22,
            padding: "28px 22px 20px",
          }}
        >
          {/* Floating pill tab — same original style */}
          <div
            style={{
              position: "absolute",
              top: -18,
              left: "50%",
              transform: "translateX(-50%)",
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              background: "rgba(0,0,0,0.82)",
              backdropFilter: "blur(8px)",
              WebkitBackdropFilter: "blur(8px)",
              color: "#fff",
              borderRadius: 999,
              padding: "3px 8px",
              height: 36,
              whiteSpace: "nowrap",
              zIndex: 2,
            }}
          >
            {TABS.map((tab) => {
              const active = currGroup === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setCurrGroup(tab.key)}
                  style={{
                    display: "flex", alignItems: "center", gap: 6,
                    padding: "4px 12px",
                    borderRadius: 999,
                    border: "none",
                    background: active ? "#fff" : "transparent",
                    color: active ? "#000" : "#fff",
                    fontWeight: 600,
                    fontSize: 13.5,
                    cursor: "pointer",
                    fontFamily: "inherit",
                    whiteSpace: "nowrap",
                  }}
                >
                  <tab.icon />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>

          {/* Search form */}
          <div className="w-full mt-1">
            {currGroup === "flight" && <FlightGroup dark />}
            {currGroup === "interstate" && <BusGroup dark />}
            {/* LagBus tab hidden — CommuteGroup still serves it when it returns. */}
            {currGroup === "train" && <TransitGroup dark />}
            {currGroup === "cowry" && <CowryGroup dark />}
          </div>
        </div>

        {/* ── Mobile: nav cards ── */}
        <div className="md:hidden h-rise hd4 grid grid-cols-2 gap-3">
          {MOBILE_CARDS.map((card) => (
            <button
              key={card.to}
              type="button"
              onClick={() => navigate(card.to)}
              style={{
                background: "rgba(255,255,255,0.10)",
                backdropFilter: "blur(16px)",
                WebkitBackdropFilter: "blur(16px)",
                border: "1px solid rgba(255,255,255,0.20)",
                borderRadius: 18,
                padding: "20px 16px",
                textAlign: "left",
                cursor: "pointer",
                color: "#fff",
                transition: "background .18s",
              }}
              onMouseOver={(e) =>
                ((e.currentTarget as HTMLElement).style.background = "rgba(255,255,255,0.16)")
              }
              onMouseOut={(e) =>
                ((e.currentTarget as HTMLElement).style.background = "rgba(255,255,255,0.10)")
              }
            >
              <div
                style={{
                  width: 42, height: 42, borderRadius: 13, background: ORANGE,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  marginBottom: 14, boxShadow: "0 6px 16px -4px rgba(255,106,26,.5)",
                }}
              >
                <card.icon style={{ color: "#fff", fontSize: 21 }} />
              </div>
              <div
                style={{
                  fontFamily: '"Bricolage Grotesque", sans-serif',
                  fontSize: 19, fontWeight: 800, marginBottom: 5,
                }}
              >
                {card.label}
              </div>
              <div
                style={{
                  fontSize: 12.5, color: "rgba(255,255,255,.54)",
                  lineHeight: 1.5, marginBottom: 16,
                }}
              >
                {card.blurb}
              </div>
              <div
                style={{
                  display: "flex", alignItems: "center", gap: 5,
                  fontSize: 13, fontWeight: 600, color: ORANGE,
                }}
              >
                {card.cta} <ArrowIcon />
              </div>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Banner;
